import { apiFetch, readErrorMessage, readJson } from '../api/client';
import type {
  BugDetail,
  BugListItem,
  BugMetrics,
  BugSummary,
  CommentDetail,
  CommentListItem,
  CreateSiteSupportMetaPayload,
  DiscussionMessage,
  FeedbackMetrics,
  FeedbackTargetKind,
  PagedResult,
  SiteContactInfo,
  SiteFeedbackApi,
  SiteSupportMetaCategory,
  SiteSupportMetaOption,
  SubmitBugPayload,
  SubmitCommentPayload,
  UpdateBugAdminPayload,
  UpdateCommentAdminPayload,
  UpdateMyTicketPayload,
  UpdateSiteSupportMetaPayload
} from './types';

async function ensureOk(res: Response): Promise<void> {
  if (!res.ok) throw new Error(await readErrorMessage(res));
}

export function createSiteFeedbackApi(): SiteFeedbackApi {
  return {
    async submitBug(payload: SubmitBugPayload) {
      const res = await apiFetch('/api/feedback/bugs', { method: 'POST', body: JSON.stringify(payload) });
      await ensureOk(res);
      return readJson<{ bugId: string }>(res);
    },
    async submitComment(payload: SubmitCommentPayload) {
      const res = await apiFetch('/api/feedback/comments', { method: 'POST', body: JSON.stringify(payload) });
      await ensureOk(res);
      return readJson<{ publicId: string; id: string }>(res);
    },
    async uploadAttachment(file: File) {
      const form = new FormData();
      form.append('file', file);
      const res = await apiFetch('/api/feedback/attachments', { method: 'POST', body: form });
      await ensureOk(res);
      return readJson<{ attachmentId: string; fileName: string; size: number }>(res);
    },
    async searchBugs(query?: string) {
      const q = query ? `?q=${encodeURIComponent(query)}` : '';
      const res = await apiFetch(`/api/feedback/bugs/search${q}`);
      await ensureOk(res);
      return readJson<BugSummary[]>(res);
    },
    async listBugs(skip = 0, take = 100) {
      const res = await apiFetch(`/api/admin/feedback/bugs?skip=${skip}&take=${take}`);
      await ensureOk(res);
      return readJson<PagedResult<BugListItem>>(res);
    },
    async getBug(bugId: string) {
      const res = await apiFetch(`/api/admin/feedback/bugs/${encodeURIComponent(bugId)}`);
      await ensureOk(res);
      return readJson<BugDetail>(res);
    },
    async updateBug(bugId: string, payload: UpdateBugAdminPayload) {
      const res = await apiFetch(`/api/admin/feedback/bugs/${encodeURIComponent(bugId)}`, {
        method: 'PATCH',
        body: JSON.stringify(payload)
      });
      await ensureOk(res);
    },
    async claimBug(bugId: string, force = false) {
      const q = force ? '?force=true' : '';
      const res = await apiFetch(`/api/admin/feedback/bugs/${encodeURIComponent(bugId)}/claim${q}`, {
        method: 'POST'
      });
      await ensureOk(res);
    },
    async deleteBug(bugId: string) {
      const res = await apiFetch(`/api/admin/feedback/bugs/${encodeURIComponent(bugId)}`, {
        method: 'DELETE'
      });
      await ensureOk(res);
    },
    async getBugMetrics() {
      const res = await apiFetch('/api/admin/feedback/bugs/metrics');
      await ensureOk(res);
      return readJson<BugMetrics>(res);
    },
    async listComments(skip = 0, take = 100) {
      const res = await apiFetch(`/api/admin/feedback/comments?skip=${skip}&take=${take}`);
      await ensureOk(res);
      return readJson<PagedResult<CommentListItem>>(res);
    },
    async getComment(commentId: string) {
      const res = await apiFetch(`/api/admin/feedback/comments/${encodeURIComponent(commentId)}`);
      await ensureOk(res);
      return readJson<CommentDetail>(res);
    },
    async updateComment(commentId: string, payload: UpdateCommentAdminPayload) {
      const res = await apiFetch(`/api/admin/feedback/comments/${encodeURIComponent(commentId)}`, {
        method: 'PATCH',
        body: JSON.stringify(payload)
      });
      await ensureOk(res);
    },
    async claimComment(commentId: string, force = false) {
      const q = force ? '?force=true' : '';
      const res = await apiFetch(`/api/admin/feedback/comments/${encodeURIComponent(commentId)}/claim${q}`, {
        method: 'POST'
      });
      await ensureOk(res);
    },
    async deleteComment(commentId: string) {
      const res = await apiFetch(`/api/admin/feedback/comments/${encodeURIComponent(commentId)}`, {
        method: 'DELETE'
      });
      await ensureOk(res);
    },
    async getFeedbackMetrics() {
      const res = await apiFetch('/api/admin/feedback/comments/metrics');
      await ensureOk(res);
      return readJson<FeedbackMetrics>(res);
    },
    async listMyBugs(skip = 0, take = 100) {
      const res = await apiFetch(`/api/feedback/my-tickets/bugs?skip=${skip}&take=${take}`);
      await ensureOk(res);
      return readJson<PagedResult<BugListItem>>(res);
    },
    async listMyFeedback(skip = 0, take = 100) {
      const res = await apiFetch(`/api/feedback/my-tickets/feedback?skip=${skip}&take=${take}`);
      await ensureOk(res);
      return readJson<PagedResult<CommentListItem>>(res);
    },
    async getMyBug(bugId: string) {
      const res = await apiFetch(`/api/feedback/my-tickets/bugs/${encodeURIComponent(bugId)}`);
      await ensureOk(res);
      return readJson<BugDetail>(res);
    },
    async getMyFeedback(publicId: string) {
      const res = await apiFetch(`/api/feedback/my-tickets/feedback/${encodeURIComponent(publicId)}`);
      await ensureOk(res);
      return readJson<CommentDetail>(res);
    },
    async updateMyBug(bugId: string, payload: UpdateMyTicketPayload) {
      const res = await apiFetch(`/api/feedback/my-tickets/bugs/${encodeURIComponent(bugId)}`, {
        method: 'PATCH',
        body: JSON.stringify(payload)
      });
      await ensureOk(res);
    },
    async updateMyFeedback(publicId: string, payload: UpdateMyTicketPayload) {
      const res = await apiFetch(`/api/feedback/my-tickets/feedback/${encodeURIComponent(publicId)}`, {
        method: 'PATCH',
        body: JSON.stringify(payload)
      });
      await ensureOk(res);
    },
    async watchTicket(kind: FeedbackTargetKind, id: string) {
      const res = await apiFetch(`/api/feedback/tickets/${encodeURIComponent(kind)}/${encodeURIComponent(id)}/watch`, {
        method: 'POST'
      });
      await ensureOk(res);
    },
    async unwatchTicket(kind: FeedbackTargetKind, id: string) {
      const res = await apiFetch(`/api/feedback/tickets/${encodeURIComponent(kind)}/${encodeURIComponent(id)}/watch`, {
        method: 'DELETE'
      });
      await ensureOk(res);
    },
    async listDiscussion(kind: FeedbackTargetKind, id: string) {
      const res = await apiFetch(`/api/feedback/discussions/${encodeURIComponent(kind)}/${encodeURIComponent(id)}`);
      await ensureOk(res);
      return readJson<DiscussionMessage[]>(res);
    },
    async postDiscussion(kind: FeedbackTargetKind, id: string, body: string) {
      const res = await apiFetch(`/api/feedback/discussions/${encodeURIComponent(kind)}/${encodeURIComponent(id)}`, {
        method: 'POST',
        body: JSON.stringify({ body })
      });
      await ensureOk(res);
      return readJson<DiscussionMessage>(res);
    },
    async deleteDiscussionMessage(messageId: string) {
      const res = await apiFetch(`/api/feedback/discussions/messages/${encodeURIComponent(messageId)}`, {
        method: 'DELETE'
      });
      await ensureOk(res);
    },
    async listActiveMeta(category: SiteSupportMetaCategory | string) {
      const res = await apiFetch(`/api/feedback/meta/${encodeURIComponent(category)}`);
      await ensureOk(res);
      return readJson<SiteSupportMetaOption[]>(res);
    },
    async listAdminMeta(category?: SiteSupportMetaCategory | string) {
      const q = category ? `?category=${encodeURIComponent(category)}` : '';
      const res = await apiFetch(`/api/admin/feedback/meta${q}`);
      await ensureOk(res);
      return readJson<SiteSupportMetaOption[]>(res);
    },
    async createMeta(payload: CreateSiteSupportMetaPayload) {
      const res = await apiFetch('/api/admin/feedback/meta', {
        method: 'POST',
        body: JSON.stringify(payload)
      });
      await ensureOk(res);
      return readJson<SiteSupportMetaOption>(res);
    },
    async updateMeta(id: string, payload: UpdateSiteSupportMetaPayload) {
      const res = await apiFetch(`/api/admin/feedback/meta/${encodeURIComponent(id)}`, {
        method: 'PUT',
        body: JSON.stringify(payload)
      });
      await ensureOk(res);
      return readJson<SiteSupportMetaOption>(res);
    },
    async recordCookieConsent(choice: string) {
      const res = await apiFetch('/api/site/cookie-consent', {
        method: 'POST',
        body: JSON.stringify({ choice })
      });
      await ensureOk(res);
    },
    async fetchSiteContact() {
      const res = await apiFetch('/api/site/contact');
      await ensureOk(res);
      return readJson<SiteContactInfo>(res);
    },
    attachmentUrl(attachmentId: string, opts?: { inline?: boolean; download?: boolean }) {
      const params = new URLSearchParams();
      if (opts?.inline) params.set('inline', 'true');
      if (opts?.download) params.set('download', 'true');
      const qs = params.toString();
      return `/api/feedback/attachments/${encodeURIComponent(attachmentId)}${qs ? `?${qs}` : ''}`;
    }
  };
}
